import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, User, ThumbsUp, MessageSquare, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Review, RoleEnum } from "../../modules";
import { useAppDispatch, useAppSelector } from "../../redux";
import { updateReview } from "../../redux/Slices/reviewSlice";
import VoteDisplayButton from "./VoteDisplayButton";
import ReplyVoteButton from "./ReplyVoteButton";

interface UserWithReviews {
  id: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  photo?: {
    path?: string;
    url?: string;
  };
  averageRating: number;
  reviewsCount: number;
  reviewsList: Review[];
}

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  professional: UserWithReviews | null;
}

const ReviewModal: React.FC<ReviewModalProps> = ({ isOpen, onClose, professional }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state: any) => state.auth);
  const [sortBy, setSortBy] = useState("recent");
  const [filterRating, setFilterRating] = useState(0);
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());
  const [replyingToReview, setReplyingToReview] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [expandedNestedReviews, setExpandedNestedReviews] = useState<Set<string>>(new Set());

  // Handle vote submission
  const handleVote = async (reviewId: string, voteType: "helpful" | "unhelpful") => {
    try {
      // You would typically call an API here to submit the vote
      console.log(`Voting ${voteType} for review ${reviewId}`);
      // Example: await dispatch(updateReviewVote({ reviewId, voteType }));
    } catch (error) {
      console.error("Error submitting vote:", error);
    }
  };


  // Toggle review expansion
  const toggleReviewExpansion = (reviewId: string) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  // Toggle nested reviews expansion
  const toggleNestedReviews = (reviewId: string) => {
    setExpandedNestedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  // Check if client can reply to a review
  const canClientReply = (review: any) => {
    if (!user || Number(user.role?.id) !== RoleEnum.client) return false;

    // Use the canReply field from the review object
    return review.canReply === true;
  };

  // Handle reply submission
  const handleReplySubmit = async (reviewId: string) => {
    if (!replyText.trim() || isSubmittingReply) return;

    setIsSubmittingReply(true);
    try {
      await dispatch(updateReview({ id: reviewId, data: { reply: replyText.trim() } }));

      // Update the local review data
      const updatedReviews = reviews.map(review =>
        review.id === reviewId
          ? { ...review, reply: replyText.trim() }
          : review
      );

      // Update the professional object with new reviews
      if (professional) {
        professional.reviewsList = updatedReviews;
      }

      setReplyingToReview(null);
      setReplyText("");
    } catch (error) {
      console.error("Failed to submit reply:", error);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  // Handle reply cancel
  const handleReplyCancel = () => {
    setReplyingToReview(null);
    setReplyText("");
  };

  // Helper function to safely render values
  const safeRender = (value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    if (typeof value === "object") {
      console.warn("Attempting to render object as string:", value);
      return "";
    }
    return String(value);
  };

  // Star rating display component for decimal ratings
  const StarRatingDisplay = ({ rating, size = "sm" }: { rating: number; size?: "sm" | "md" | "lg" }) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    const sizeClasses = {
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6"
    };

    return (
      <div className="flex items-center">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={i} className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
        ))}
        {hasHalfStar && (
          <div className="relative">
            <Star className={`${sizeClasses[size]} text-gray-300`} />
            <div className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
              <Star className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
            </div>
          </div>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={i + fullStars + (hasHalfStar ? 1 : 0)} className={`${sizeClasses[size]} text-gray-300`} />
        ))}
      </div>
    );
  };

  if (!isOpen || !professional) return null;

  // Debug logging
  console.log("ReviewModal professional object:", professional);

  // Clean the professional object to ensure only primitive values
  const cleanProfessional = {
    id: String(professional.id || ""),
    name: String(professional.name || professional.title || "Professional"),
    firstName: String(professional.firstName || ""),
    lastName: String(professional.lastName || ""),
    title: String(professional.title || ""),
    photo: professional.photo ? {
      path: String(professional.photo.path || ""),
      url: String(professional.photo.url || "")
    } : null,
    averageRating: Number(professional.averageRating) || 0,
    reviewsCount: Number(professional.reviewsCount) || 0,
    reviewsList: Array.isArray(professional.reviewsList) ? professional.reviewsList : []
  };

  // Get professional name
  const professionalName = cleanProfessional.name ||
    `${cleanProfessional.firstName} ${cleanProfessional.lastName}`.trim() ||
    cleanProfessional.title ||
    "Professional";

  // Get reviews from clean professional object
  const reviews = cleanProfessional.reviewsList;

  // If no reviews are available, show a message
  const hasReviews = reviews.length > 0;

  // Use the provided average rating
  const averageRating = cleanProfessional.averageRating;

  // Get review count
  const reviewCount = cleanProfessional.reviewsCount;

  // Ensure averageRating is a valid number
  const safeAverageRating = isNaN(averageRating) ? 0 : averageRating;

  // Clean and sort reviews
  const cleanReviews = reviews.map(review => {
    // Safely extract author name
    const authorFirstName = review.author?.firstName || "";
    const authorLastName = review.author?.lastName || "";
    const authorName = authorFirstName && authorLastName
      ? `${authorFirstName} ${authorLastName}`.trim()
      : authorFirstName || authorLastName || "Anonymous";

    // Safely extract author company/title
    const authorCompany = review.author?.title || "";

    // Safely extract author image
    const authorImage = review.author?.photo?.path || "";
    return {
      id: String(review.id || ""),
      author: String(authorName),
      rating: Number(review.rating) || 0,
      date: String(review.createdAt || ""),
      comment: String(review.text || ""),
      authorCompany: String(authorCompany),
      authorImage: authorImage ? String(authorImage) : undefined,
      helpful: Number(review.helpful) || 0,
      reply: String(review.reply || ""),
      review: review.review || null,
      votes: review.votes || []
    };
  });

  const sortedReviews = [...cleanReviews].sort((a, b) => {
    switch (sortBy) {
      case "rating":
        return b.rating - a.rating;
      case "helpful":
        return b.helpful - a.helpful;
      default:
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  }).filter(review => filterRating === 0 || review.rating === filterRating);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        >
          <div className="p-6 border-b sticky top-0 bg-white">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-semibold">{t("reviewModal.reviewsFor", { name: safeRender(professionalName) })}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <StarRatingDisplay rating={safeAverageRating} size="sm" />
                  <span className="text-sm text-gray-600">
                    {safeRender(safeAverageRating.toFixed(1))} ({t("reviewModal.reviews", { count: reviewCount })})
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex gap-4 mt-4">
              <select
                value={filterRating}
                onChange={(e) => setFilterRating(Number(e.target.value))}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value={0}>{t("reviewModal.allRatings")}</option>
                <option value={5}>{t("reviewModal.fiveStars")}</option>
                <option value={4}>{t("reviewModal.fourStars")}</option>
                <option value={3}>{t("reviewModal.threeStars")}</option>
                <option value={2}>{t("reviewModal.twoStars")}</option>
                <option value={1}>{t("reviewModal.oneStar")}</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="recent">{t("reviewModal.mostRecent")}</option>
                <option value="rating">{t("reviewModal.highestRating")}</option>
                <option value="helpful">{t("reviewModal.mostHelpful")}</option>
              </select>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {hasReviews ? (
              sortedReviews.map((review) => (
                <div key={review.id} className="border-b last:border-0 pb-6 last:pb-0">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        {review.authorImage ? (
                          <img
                            src={review.authorImage}
                            alt={review.author}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <User className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{safeRender(review.author)}</h4>
                        </div>
                        {review.authorCompany && (
                          <p className="text-sm text-gray-500">{safeRender(review.authorCompany)}</p>
                        )}
                        <p className="text-sm text-gray-500">{safeRender(review.date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <StarRatingDisplay rating={review.rating} size="sm" />
                    </div>
                  </div>

                  <div className="mt-3">
                    <p className="text-gray-700">
                      {(() => {
                        const comment = safeRender(review.comment);
                        const isExpanded = expandedReviews.has(review.id);
                        const shouldTruncate = comment.length > 200;

                        if (!shouldTruncate || isExpanded) {
                          return comment;
                        }

                        return comment.substring(0, 200) + "...";
                      })()}
                    </p>

                    {(() => {
                      const comment = safeRender(review.comment);
                      const isExpanded = expandedReviews.has(review.id);
                      const shouldTruncate = comment.length > 200;

                      if (shouldTruncate) {
                        return (
                          <button
                            onClick={() => toggleReviewExpansion(review.id)}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            {isExpanded ? t("reviewModal.showLess") : t("reviewModal.seeMore")}
                          </button>
                        );
                      }
                      return null;
                    })()}



                    <div className="mt-3 flex items-center gap-4">
                      <VoteDisplayButton
                        reviewId={review.id}
                        votes={(review as any).votes || []}
                        currentUserId={user?.id}
                        className=""
                      />

                      {/* See More Button - Show when there are nested reviews */}
                      {(review.review && Array.isArray(review.review) && review.review.length > 0 || review.reply) && (
                        <button
                          onClick={() => toggleNestedReviews(review.id)}
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          <MessageSquare className="h-4 w-4" />
                          {expandedNestedReviews.has(review.id) ? t("reviewModal.showLess") : `${t("reviewModal.seeMore")} (${review.review?.length || 0})`}
                        </button>
                      )}

                      {/* Reply Button - Only show for clients who can reply */}
                      {canClientReply(review) && !review.reply && (
                        <button
                          onClick={() => setReplyingToReview(review.id)}
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          <MessageSquare className="h-4 w-4" />
                          {t("reviewModal.reply")}
                        </button>
                      )}
                    </div>

                    {/* Reply Form */}
                    {replyingToReview === review.id && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("reviewModal.yourReply")}
                          </label>
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder={t("reviewModal.writeReply")}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            rows={3}
                            maxLength={500}
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            {t("reviewModal.charactersLimit", { current: replyText.length })}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleReplySubmit(review.id)}
                            disabled={!replyText.trim() || isSubmittingReply}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            <Send className="h-4 w-4" />
                            {isSubmittingReply ? t("reviewModal.submitting") : t("reviewModal.submitReply")}
                          </button>
                          <button
                            onClick={handleReplyCancel}
                            disabled={isSubmittingReply}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            {t("reviewModal.cancel")}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Nested Reviews Display */}
                    {expandedNestedReviews.has(review.id) && review.review && Array.isArray(review.review) && review.review.length > 0 && (
                      <div className="mt-4 pl-6 border-l-4 border-gray-200">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">{t("reviewModal.relatedReviews", { count: review.review?.length || 0 })}</h4>
                          <div className="space-y-3">
                            {review.review.map((nestedReview: any, index: number) => (
                              <div key={index} className="p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                    <User className="h-4 w-4 text-gray-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h5 className="font-medium text-gray-900 text-sm">
                                        {nestedReview.author?.firstName && nestedReview.author?.lastName
                                          ? `${nestedReview.author.firstName} ${nestedReview.author.lastName}`.trim()
                                          : nestedReview.author?.id || "Anonymous"}
                                      </h5>
                                      <div className="flex items-center gap-1">
                                        <StarRatingDisplay rating={nestedReview.rating || 0} size="sm" />
                                        <span className="text-xs text-gray-500">{nestedReview.rating || 0}/5</span>
                                      </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-2">
                                      {new Date(nestedReview.createdAt || Date.now()).toLocaleDateString()}
                                    </p>
                                    {nestedReview.text && (
                                      <p className="text-gray-700 text-sm leading-relaxed">
                                        {nestedReview.text}
                                      </p>
                                    )}
                                    {nestedReview.reply && (
                                      <div className="mt-2 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                        <p className="text-sm font-medium text-blue-900">
                                          {user && Number(user.role?.id) === RoleEnum.client ? t("reviewModal.clientResponse") : t("reviewModal.officialResponse")}
                                        </p>
                                        <p className="mt-1 text-sm text-blue-800">{nestedReview.reply}</p>
                                        <ReplyVoteButton
                                          reviewId={nestedReview.id}
                                          hasReply={!!nestedReview.reply}
                                          votes={nestedReview.votes || []}
                                          currentUserId={user?.id}
                                        />
                                      </div>
                                    )}

                                    {/* Reply button for nested reviews */}
                                    {canClientReply(nestedReview) && !nestedReview.reply && (
                                      <div className="mt-3">
                                        <button
                                          onClick={() => setReplyingToReview(`${review.id}-${index}`)}
                                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                        >
                                          <MessageSquare className="h-4 w-4" />
                                          {t("reviewModal.reply")}
                                        </button>
                                      </div>
                                    )}

                                    {/* Reply form for nested reviews */}
                                    {replyingToReview === `${review.id}-${index}` && (
                                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="mb-3">
                                          <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t("reviewModal.yourReply")}
                                          </label>
                                          <textarea
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            placeholder={t("reviewModal.writeReply")}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                            rows={3}
                                            maxLength={500}
                                          />
                                          <div className="text-xs text-gray-500 mt-1">
                                            {t("reviewModal.charactersLimit", { current: replyText.length })}
                                          </div>
                                        </div>
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => handleReplySubmit(nestedReview.id)}
                                            disabled={!replyText.trim() || isSubmittingReply}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                          >
                                            <MessageSquare className="h-3 w-3" />
                                            {isSubmittingReply ? t("reviewModal.submitting") : t("reviewModal.submitReply")}
                                          </button>
                                          <button
                                            onClick={handleReplyCancel}
                                            disabled={isSubmittingReply}
                                            className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                          >
                                            {t("reviewModal.cancel")}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="pt-4">
                          {/* Reply Section */}
                          {review.reply && (
                            <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-100">
                              <p className="text-sm font-medium text-blue-900">
                                {user && Number(user.role?.id) === RoleEnum.client ? t("reviewModal.clientResponse") : t("reviewModal.officialResponse")}
                              </p>
                              <p className="mt-1 text-sm text-blue-800">
                                {(() => {
                                  const reply = safeRender(review.reply);
                                  const isExpanded = expandedReviews.has(`${review.id}-reply`);
                                  const shouldTruncate = reply.length > 200;

                                  if (!shouldTruncate || isExpanded) {
                                    return reply;
                                  }

                                  return reply.substring(0, 200) + "...";
                                })()}
                              </p>

                              {(() => {
                                const reply = safeRender(review.reply);
                                const isExpanded = expandedReviews.has(`${review.id}-reply`);
                                const shouldTruncate = reply.length > 200;

                                if (shouldTruncate) {
                                  return (
                                    <button
                                      onClick={() => toggleReviewExpansion(`${review.id}-reply`)}
                                      className="mt-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                    >
                                      {isExpanded ? t("reviewModal.showLess") : t("reviewModal.seeMore")}
                                    </button>
                                  );
                                }
                                return null;
                              })()}

                              <ReplyVoteButton
                                reviewId={review.id}
                                hasReply={!!review.reply}
                                votes={(review as any).votes || []}
                                currentUserId={user?.id}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}


                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <Star className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t("reviewModal.noReviewsYet")}</h3>
                    <p className="text-gray-500">
                      {t("reviewModal.noReviewsDesc")}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReviewModal;